/**
 * Ressource unique entre les onglets d'AGORA (verrou Web Locks du navigateur, même origine) : le softphone, un
 * enregistrement Scribe. Le premier onglet qui la demande la détient ; les autres sont « ailleurs » et attendent leur
 * tour : quand l'onglet qui la détient se ferme ou la rend, le suivant la reçoit sans action. `prendre()` (bouton
 * « Utiliser ici ») la reprend tout de suite : l'onglet qui la détenait la perd et se remet en attente.
 * Navigateur sans Web Locks : chaque onglet la détient (comportement d'avant le verrou).
 */
import React from 'react';

/** `inactif` : pas demandée ; `detenu` : cet onglet l'a ; `ailleurs` : un autre onglet l'a, celui-ci attend. */
export type EtatVerrou = 'inactif' | 'detenu' | 'ailleurs';

export interface VerrouOnglet {
  etat: EtatVerrou;
  /** Reprend la ressource dans cet onglet (l'autre onglet la perd). */
  prendre: () => void;
}

const verrous = (): LockManager | undefined => (typeof navigator !== 'undefined' ? navigator.locks : undefined);

export function useVerrouOnglet(nom: string, actif: boolean): VerrouOnglet {
  const [etat, setEtat] = React.useState<EtatVerrou>('inactif');
  // Demande en cours (tenue ou en attente) : la terminer rend le verrou et abandonne l'attente.
  const demande = React.useRef<(() => void) | null>(null);

  const demander = React.useCallback((voler: boolean) => {
    const v = verrous();
    demande.current?.();
    if (!v) { setEtat('detenu'); return; }
    const abandon = new AbortController();
    let rendre = () => {};
    const tenue = new Promise<void>((r) => { rendre = r; });
    demande.current = () => { abandon.abort(); rendre(); };
    // Verrou accordé : gardé jusqu'à la fin de la demande (désactivation, démontage, nouvelle demande).
    const tenir = () => { setEtat('detenu'); return tenue; };
    const attendre = () => {
      if (abandon.signal.aborted) return;
      setEtat('ailleurs');
      v.request(nom, { signal: abandon.signal }, tenir).catch(perdu);
    };
    // Verrou repris par un autre onglet (« Utiliser ici ») : on se remet en attente.
    const perdu = () => { if (!abandon.signal.aborted) attendre(); };
    if (voler) v.request(nom, { steal: true }, tenir).catch(perdu);
    else v.request(nom, { ifAvailable: true }, (verrou) => (verrou ? tenir() : (attendre(), undefined))).catch(perdu);
  }, [nom]);

  React.useEffect(() => {
    if (!actif) { setEtat('inactif'); return; }
    demander(false);
    return () => { demande.current?.(); demande.current = null; };
  }, [actif, demander]);

  const prendre = React.useCallback(() => { if (actif) demander(true); }, [actif, demander]);
  return React.useMemo(() => ({ etat, prendre }), [etat, prendre]);
}
